import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from 'react-router-dom';
import type { ContactListItem, ListResult } from '@tww/shared';
import { contractModeOf, contractStatus, fmtDate, fmtEur, fmtNum } from '@tww/shared';
import { api } from '../../api/client';
import { useCategoryDefs, useLookups } from '../../api/hooks';
import { useAuth } from '../../auth/AuthProvider';
import { DataTable, Pager, type Column } from '../../ui/DataTable';
import { ErrorState, RatingBadge, Skeleton } from '../../ui/basics';
import { Icon } from '../../ui/Icon';
import { useListState } from '../../ui/useListState';
import { attrLabel } from './attributes';
import { Dropdown } from '../../ui/Dropdown';

const name = (c: ContactListItem) => (
  <>
    <Link className="link" to={`/kontakte/${c.id}`}>
      {c.display_name}
    </Link>
    {(c.organisation_name || c.position) && <span className="sm">{[c.organisation_name, c.position].filter(Boolean).join(', ')}</span>}
    {c.archived_at && <span className="sm">archiviert</span>}
    {c.review_needed && <span className="sm miss">zu prüfen</span>}
  </>
);
// ohne Bewertung bleibt die Zelle leer
const rating = (c: ContactListItem) => (c.rating_grade ? <RatingBadge grade={c.rating_grade} reason={c.rating_reason} at={c.rated_at} /> : null);
const regionCol = (c: ContactListItem) => (
  <>
    {c.state_label ?? '–'}
    {c.region && <span className="sm">{c.region}</span>}
  </>
);
const contractCol = (c: ContactListItem) => {
  const st = contractStatus(contractModeOf(c.attributes), c.contract_year);
  return <span className={st.missing ? 'miss' : 'ok'}>{st.text}</span>;
};
const priceCol = (c: ContactListItem) =>
  c.last_price ? (
    <>
      {fmtEur(c.last_price.price)} <span className="mut">({c.last_price.year})</span>
    </>
  ) : (
    '–'
  );

/** Kontaktliste: kompakte Filterleiste (Kategorie, Bundesland, Region als Auswahl), Spalten je Kategorie */
export function ContactsListPage({ partnerView }: { partnerView?: boolean }) {
  const nav = useNavigate();
  const { can } = useAuth();
  const defs = useCategoryDefs();
  const lookups = useLookups();
  const { state, update, setFilter, toggleSort, apiParams } = useListState({ sort: 'display_name', order: 'asc' });

  const category = partnerView ? 'partner' : (state.filters.category?.[0] ?? '');
  const params = partnerView ? `${apiParams}&filter[category]=partner` : apiParams;
  const q = useQuery({ queryKey: ['contacts', params], queryFn: () => api.get<ListResult<ContactListItem>>(`/contacts?${params}`), placeholderData: (p) => p });
  const def = defs.data?.find((d) => d.key === category);
  const catField = (key: string) => def?.fields.find((f) => f.key === key);
  const attr = (c: ContactListItem, key: string) => attrLabel(catField(key), c.attributes[category]?.[key], lookups.data);

  const columns = useMemo<Column<ContactListItem>[]>(() => {
    const A: Column<ContactListItem> = { key: 'rating', label: 'Bewertung', sortable: true, width: 90, render: rating };
    const N: Column<ContactListItem> = { key: 'display_name', label: 'Name', sortable: true, render: name };
    const R: Column<ContactListItem> = { key: 'region', label: 'Region', sortable: true, render: regionCol };
    const C: Column<ContactListItem> = { key: 'city', label: 'Ort', sortable: true, render: (c) => c.city ?? '–' };
    const ACT: Column<ContactListItem> = { key: 'actions', label: 'Aktionen', num: true, render: (c) => c.action_count };
    const LAST: Column<ContactListItem> = { key: 'last_action_at', label: 'Letzte Aktion', sortable: true, render: (c) => (c.last_action_at ? fmtDate(c.last_action_at) : '–') };
    const NEXT: Column<ContactListItem> = { key: 'next_action_at', label: 'Nächste Aktion', sortable: true, render: (c) => (c.next_action_at ? fmtDate(c.next_action_at) : '–') };
    const TREES: Column<ContactListItem> = { key: 'partner_trees', label: 'Bäume', num: true, sortable: true, render: (c) => (c.partner_trees ? fmtNum(c.partner_trees) : '–') };
    const DON: Column<ContactListItem> = { key: 'partner_donation', label: 'Spende gesamt', num: true, sortable: true, render: (c) => (c.partner_donation ? fmtEur(c.partner_donation) : '–') };
    switch (category) {
      case 'forester':
        return [A, N, R, { key: 'last_price', label: 'Letzter Baumpreis', sortable: true, render: priceCol }, { key: 'contract', label: 'Vertrag', render: contractCol }, ACT, { key: 'own', label: 'Waldbesitzart', render: (c) => attr(c, 'ownership_types') || '–' }, { key: 'culture_care', label: 'Kulturpflege im Sommer', render: (c) => attr(c, 'culture_care') || '–' }];
      case 'owner':
        return [A, N, R, { key: 'contract', label: 'Vertrag', render: contractCol }, { key: 'type', label: 'Waldbesitzart', render: (c) => attr(c, 'ownership_type') || '–' }];
      case 'partner':
        return [N, { key: 'industry', label: 'Branche', render: (c) => attr(c, 'industry') || '–' }, { key: 'code', label: 'Partnercode', render: (c) => attr(c, 'partner_code') || '–' }, ACT, TREES, DON, LAST, NEXT];
      case 'guide':
      case 'educator':
        return [A, N, { key: 'dep', label: 'Einsatzregion', render: (c) => attr(c, 'deployment_region') || c.region || '–' }, { key: 'einsaetze', label: 'Einsätze', num: true, render: (c) => c.action_count }, LAST, NEXT, { key: 'mobile', label: 'Mobil', render: (c) => c.mobile ?? c.phone ?? '–' }];
      case 'hotel':
        return [A, N, C, R, LAST, { key: 'reason', label: 'Begründung', render: (c) => <span className="mut">{(c.rating_reason ?? '').slice(0, 60)}</span> }];
      case 'wc':
        return [A, N, C, R, { key: 'type', label: 'Art', render: (c) => attr(c, 'wc_type') || '–' }, LAST];
      case 'supplier':
        return [A, N, { key: 'type', label: 'Art', render: (c) => attr(c, 'supplier_type') || '–' }, C, R, LAST];
      default:
        return [A, N, { key: 'cats', label: 'Kategorien', render: (c) => c.categories.map((k) => defs.data?.find((d) => d.key === k)?.name ?? k).join(', ') }, C, R, { key: 'contact', label: 'Kontakt', render: (c) => <>{c.mobile ?? c.phone ?? '–'}{c.email && <span className="sm">{c.email}</span>}</> }];
    }
  }, [category, defs.data, lookups.data]);

  const facets = q.data?.facets ?? {};
  const cnt = (facet: string, value: string) => facets[facet]?.find((f) => f.value === value)?.count;
  const withCount = (label: string, n: number | undefined) => (n != null ? `${label} (${n})` : label);
  const states = lookups.data?.find((l) => l.key === 'states')?.values ?? [];
  const catOptions = (defs.data ?? []).filter((d) => d.is_active).map((d) => ({ value: d.key, label: withCount(d.name_plural, cnt('category', d.key)) }));
  const stateOptions = (facets.state ?? []).map((f) => ({ value: f.value, label: withCount(states.find((s) => s.code === f.value)?.label ?? f.value, f.count) })).sort((a, b) => a.label.localeCompare(b.label));
  const regionOptions = (facets.region ?? []).map((f) => ({ value: f.value, label: withCount(f.value, f.count) })).sort((a, b) => a.label.localeCompare(b.label));
  const title = partnerView ? 'Spendenpartner' : def ? def.name_plural : 'Kontakte';
  const stateSel = state.filters.state?.[0] ?? '';
  const regionSel = state.filters.region?.[0] ?? '';
  const hasFilter = Boolean(category && !partnerView) || Boolean(stateSel) || Boolean(regionSel) || Boolean(state.q) || Boolean(state.filters.archived?.length) || Boolean(state.filters.review_needed?.length);
  const newHref = partnerView ? '/kontakte/neu?art=organisation&kategorie=partner' : category ? `/kontakte/neu?kategorie=${category}` : '/kontakte/neu';

  return (
    <>
      <div className="head">
        <div>
          <h1>{title}</h1>
          <div className="sub">{q.data ? `${q.data.total} ${partnerView ? 'Spendenpartner' : 'Kontakte'}` : ''}</div>
        </div>
        <div className="actions">
          {can('contacts', 'write') && (
            <Link className="btn primary" to={newHref}>
              <Icon name="plus" /> {partnerView ? 'Neuer Spendenpartner' : 'Neuer Kontakt'}
            </Link>
          )}
        </div>
      </div>
      <div className="filterbar">
        <div className="field grow">
          <label htmlFor="f-q">Suche</label>
          <input id="f-q" value={state.q} onChange={(e) => update({ q: e.target.value })} placeholder="Name, Organisation, Ort, E-Mail" />
        </div>
        {!partnerView && (
          <div className="field">
            <label htmlFor="f-cat">Kategorie</label>
            <Dropdown id="f-cat" value={category} onChange={(v) => update({ filters: { ...state.filters, category: v ? [v] : [], region: [] } })} placeholder="Alle Kategorien" options={catOptions.map((o) => ({ value: String(o.value), label: o.label }))} />
          </div>
        )}
        <div className="field">
          <label htmlFor="f-state">Bundesland</label>
          <Dropdown id="f-state" value={stateSel} onChange={(v) => update({ filters: { ...state.filters, state: v ? [v] : [], region: [] } })} placeholder="Alle" options={stateOptions.map((o) => ({ value: String(o.value), label: o.label }))} />
        </div>
        <div className="field">
          <label htmlFor="f-region">Region</label>
          <Dropdown id="f-region" value={regionSel} onChange={(v) => setFilter('region', v ? [v] : [])} placeholder="Alle" options={regionOptions.map((o) => ({ value: String(o.value), label: o.label }))} />
        </div>
        <div className="spacer" />
        <div className="opts">
          <label>
            <input type="checkbox" checked={state.filters.archived?.[0] === '1'} onChange={(e) => setFilter('archived', e.target.checked ? ['1'] : [])} /> archivierte
          </label>
          {can('contacts', 'write') && (
            <label>
              <input type="checkbox" checked={state.filters.review_needed?.[0] === '1'} onChange={(e) => setFilter('review_needed', e.target.checked ? ['1'] : [])} /> zu prüfen
            </label>
          )}
          {hasFilter && (
            <button type="button" className="btn sm" onClick={() => update({ q: '', filters: {} })}>
              Zurücksetzen
            </button>
          )}
        </div>
      </div>
      <div className="panel">
        {q.isLoading ? (
          <Skeleton rows={8} />
        ) : q.isError ? (
          <ErrorState error={q.error} retry={() => q.refetch()} />
        ) : (
          <DataTable
            columns={columns}
            rows={q.data!.items}
            sort={state.sort ? { key: state.sort, order: state.order ?? 'asc' } : undefined}
            onSort={toggleSort}
            onRowClick={(c) => nav(`/kontakte/${c.id}`)}
            rowClass={(c) => (c.archived_at ? 'archived' : '')}
            empty={state.q ? `Keine Kontakte für „${state.q}“` : 'Noch keine Kontakte in dieser Auswahl'}
            footer={<Pager page={q.data!.page} pageSize={q.data!.pageSize} total={q.data!.total} onPage={(p) => update({ page: p })} />}
          />
        )}
      </div>
    </>
  );
}
